import { Autocomplete, Box, ClickAwayListener, Paper, styled, TextField, useTheme } from '@mui/material'
import { useGetCustomersWithAdminUserPermission } from 'src/api/customer'
import { useSidebarCtx } from 'src/hooks/context-hooks'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { ICustomer } from 'src/generated-types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AppRoutesNavigation } from 'src/app-routes'
import { CenteredSpinner } from '../Spinner'
import CustomerDropDownSelectedItem from './CustomerDropDownSelectedItem'

const CustomerDropDown = () => {
  const { data: userCustomers, isLoading } = useGetCustomersWithAdminUserPermission()
  const { collapsibleSidebarOpen } = useSidebarCtx()
  const { t: translate } = useTranslation(['admin-sidebar', 'common'])
  const [searchText, setSearchText] = useState<string>('')
  const [autoOpen, setAutoOpen] = useState(false)
  const [value, setValue] = useState<ICustomer | null>(userCustomers ? userCustomers[0] : null)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { id: urlCustomerId } = useParams()

  const SEARCH_OPTION = {
    id: -1,
    name: '',
  } as ICustomer

  const options = useMemo(() => {
    if (!userCustomers) return []
    const filteredCustomers = userCustomers.filter(c => c.name.toLowerCase().includes(searchText.toLowerCase()))
    return [SEARCH_OPTION, ...filteredCustomers]
  }, [userCustomers, searchText])

  useEffect(() => {
    const storedCustomerId = localStorage.getItem('selectedCustomerAdminCustomerId')
    let selectedCustomer: ICustomer | undefined = undefined
    if (urlCustomerId && urlCustomerId != storedCustomerId && userCustomers) {
      selectedCustomer = userCustomers.find(customer => customer.id.toString() === urlCustomerId)
    } else if (storedCustomerId && userCustomers) {
      selectedCustomer = userCustomers.find(customer => customer.id.toString() === storedCustomerId)
    } else {
      selectedCustomer = userCustomers ? userCustomers[0] : undefined
    }
    if (selectedCustomer) {
      setValue(selectedCustomer)
      if (selectedCustomer.hasLocalities) localStorage.setItem('currentCustomerId', selectedCustomer.id.toString())
    }
  }, [location.pathname, userCustomers, urlCustomerId])

  const onSelectCustomer = (customer: ICustomer) => {
    localStorage.setItem('selectedCustomerAdminCustomerId', customer.id.toString())
    if (customer.hasLocalities) localStorage.setItem('currentCustomerId', customer.id.toString())
    setValue(customer)
    if (customer.isGlobalServiceStationProvider) {
      navigate(AppRoutesNavigation.Customer.serviceStations(customer.id))
    } else {
      navigate(AppRoutesNavigation.Customer.localities(customer.id))
    }
  }
  if (isLoading) return <CenteredSpinner size={40} />

  return (
    <Box padding="0px !important" height={'64px'} marginLeft={'-4px'}>
      <ClickAwayListener onClickAway={() => setAutoOpen(false)}>
        <Autocomplete
          open={autoOpen}
          onOpen={() => setAutoOpen(true)}
          onClose={() => setAutoOpen(false)}
          options={options ?? []}
          getOptionLabel={o => o.name}
          clearIcon={false}
          onChange={(e, v) => {
            onSelectCustomer(v as ICustomer)
            setSearchText('')
          }}
          value={value}
          PaperComponent={props => <Paper {...props} sx={{ boxShadow: '2px 8px 8px #00000040' }} />}
          ListboxProps={{
            style: {
              maxHeight: 800,
              overflow: 'auto',
              backgroundColor: theme.palette.white.main,
              boxShadow: `2px 2px 4px ${theme.palette.darkTransparentBlack.main}`,
              fontSize: '14px',
            },
          }}
          renderOption={(props: any, option) => {
            if (option.id === -1) {
              return (
                <Box {...props} key={option.id} onClick={e => e.stopPropagation()} sx={{ backgroundColor: 'white' }}>
                  <TextField
                    autoFocus
                    size="small"
                    placeholder={translate('search', { ns: 'common' })}
                    fullWidth
                    onKeyDown={e => e.stopPropagation()}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                </Box>
              )
            }
            return (
              <Box {...props} key={option.id}>
                {option.name}
              </Box>
            )
          }}
          renderInput={params => (
            <CustomTextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: {
                  display: 'none',
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: value && (
                  <CustomerDropDownSelectedItem customer={value} collapsibleSidebarOpen={collapsibleSidebarOpen} />
                ),
              }}
            />
          )}
        />
      </ClickAwayListener>
    </Box>
  )
}

export default CustomerDropDown

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    paddingLeft: '8px !important',
    paddingRight: '16px !important',
  },
  '& .MuiInputBase-root': {
    height: '64px',
    alignContent: 'center',
    paddingRight: '8px !important',
  },
}))
